import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Chip, Snackbar, Alert,
  FormControl, InputLabel, Select, MenuItem, Grid
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  LockReset as LockIcon, Person as PersonIcon
} from '@mui/icons-material';
import api from '../services/api';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'vendedor',
    estado: 'activo'
  });

  const [passwordForm, setPasswordForm] = useState({
    userId: null,
    password: ''
  });

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      const res = await api.get('/auth/usuarios');
      setUsuarios(res.data.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Error cargando usuarios', severity: 'error' });
    }
  };

  const handleOpenNuevo = () => {
    setEditando(null);
    setForm({ nombre: '', email: '', password: '', rol: 'vendedor', estado: 'activo' });
    setOpenDialog(true);
  };

  const handleOpenEditar = (usuario) => {
    setEditando(usuario);
    setForm({
      nombre: usuario.nombre,
      email: usuario.email,
      password: '',
      rol: usuario.rol,
      estado: usuario.estado
    });
    setOpenDialog(true);
  };

  const handleOpenPassword = (usuario) => {
    setPasswordForm({ userId: usuario.id, password: '' });
    setOpenPasswordDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.nombre || !form.email || (!editando && !form.password)) {
        setSnackbar({ open: true, message: 'Complete todos los campos obligatorios', severity: 'error' });
        return;
      }

      if (editando) {
        await api.put(`/auth/usuarios/${editando.id}`, form);
        setSnackbar({ open: true, message: 'Usuario actualizado correctamente', severity: 'success' });
      } else {
        await api.post('/auth/usuarios', form);
        setSnackbar({ open: true, message: 'Usuario creado correctamente', severity: 'success' });
      }

      setOpenDialog(false);
      cargarUsuarios();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error al guardar', severity: 'error' });
    }
  };

  const handleResetPassword = async () => {
    try {
      if (!passwordForm.password || passwordForm.password.length < 6) {
        setSnackbar({ open: true, message: 'La contraseña debe tener al menos 6 caracteres', severity: 'error' });
        return;
      }

      await api.put(`/auth/usuarios/${passwordForm.userId}/reset-password`, {
        password: passwordForm.password
      });

      setSnackbar({ open: true, message: 'Contraseña actualizada correctamente', severity: 'success' });
      setOpenPasswordDialog(false);
    } catch (error) {
      setSnackbar({ open: true, message: 'Error al cambiar contraseña', severity: 'error' });
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de desactivar este usuario?')) return;

    try {
      await api.delete(`/auth/usuarios/${id}`);
      setSnackbar({ open: true, message: 'Usuario desactivado correctamente', severity: 'success' });
      cargarUsuarios();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error al desactivar', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Gestión de Usuarios
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNuevo} sx={{ backgroundColor: '#1976d2' }}>
          Nuevo Usuario
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rol</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Último Acceso</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" />
                    {user.nombre}
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.rol === 'admin' ? 'Administrador' : 'Vendedor'}
                    color={user.rol === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.estado}
                    color={user.estado === 'activo' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.ultimo_acceso
                    ? new Date(user.ultimo_acceso).toLocaleString('es-PE')
                    : 'Nunca'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenEditar(user)} title="Editar">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="warning" onClick={() => handleOpenPassword(user)} title="Cambiar contraseña">
                      <LockIcon />
                    </IconButton>
                    {user.id !== 1 && (
                      <IconButton size="small" color="error" onClick={() => handleEliminar(user.id)} title="Desactivar">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Crear/Editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre Completo *"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Correo Electrónico *"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </Grid>
            {!editando && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contraseña *"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  helperText="Mínimo 6 caracteres"
                  required
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Rol *</InputLabel>
                <Select
                  value={form.rol}
                  label="Rol *"
                  onChange={(e) => setForm({ ...form, rol: e.target.value })}
                >
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="vendedor">Vendedor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editando && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={form.estado}
                    label="Estado"
                    onChange={(e) => setForm({ ...form, estado: e.target.value })}
                  >
                    <MenuItem value="activo">Activo</MenuItem>
                    <MenuItem value="inactivo">Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: '#1976d2' }}>
            {editando ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Cambiar Contraseña */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#f57c00', fontWeight: 'bold' }}>
          Cambiar Contraseña
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nueva Contraseña"
            type="password"
            value={passwordForm.password}
            onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
            helperText="Mínimo 6 caracteres"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleResetPassword} variant="contained" color="warning">
            Cambiar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default Usuarios;